<template>
    <BarChart
        class="dashboard-chart mt-2"
        :data="chartData"
        :options="chartOptions"
        :styles="styles"
    />
</template>
<script>
import { computed, toRefs, ref } from '@vue/composition-api'
import useCommonData from '@/composables/useCommonData'
import useSelectMonth from '@/composables/useSelectMonth'
import useXAxisData from '@/composables/useXAxisData'
import { useUtils as useI18nUtils } from '@core/libs/i18n'
import CreateVueChart from '@/components/CreateVueChart'
export default {
    components: {
        BarChart: CreateVueChart('Bar')
    },
    props: {
        styles: {
            type: Object,
            default: function () {
                return {}
            }
        },
        datasets: {
            type: Array,
            default: function () {
                return []
            }
        }
    },
    setup (props, context) {
        const { datasets } = toRefs(props)
        const { selectedMonth } = useSelectMonth()
        const { __toKFormat, __getDollar } = useCommonData()
        const { createThisMonthFullDate } = useXAxisData()
        const productCode = ref(null)
        const date = ref(null)
        const chartData = computed(() => ({
            labels: createThisMonthFullDate(selectedMonth.value),
            datasets: datasets.value
        }))
        const { t } = useI18nUtils()
        const chartOptions = computed(() => ({
            onClick: (e, chartData) => {
                if (date.value && productCode.value) {
                    context.emit('click:barChart', { xLabel: date.value, productCode: productCode.value })
                }
            },
            onHover: function (e, el) {
                e.target.style.cursor = el.length ? 'pointer' : 'default'
            },
            tooltips: {
                enabled: false,
                yPadding: 10,
                xPadding: 10,
                custom: function (tooltipModel) {
                    productCode.value = null
                    if (tooltipModel.title) {
                        date.value = tooltipModel.title[0]
                    }

                    let tooltipEl = document.getElementById('chartjs-tooltip')
                    if (!tooltipEl) {
                        tooltipEl = document.createElement('div')
                        tooltipEl.id = 'chartjs-tooltip'
                        tooltipEl.innerHTML = `
                            <div id="chartjs-tooltip-title" style="border-bottom: 1px solid #DBDBDB;"></div>
                            <div id="chartjs-tooltip-body"></div>
                        `
                        document.body.appendChild(tooltipEl)
                    }
                    if (tooltipModel.opacity === 0) {
                        tooltipEl.style.opacity = 0
                        return
                    }
                    tooltipEl.classList.add('bg-white')
                    tooltipEl.classList.remove('above', 'below', 'no-transform')
                    if (tooltipModel.yAlign) {
                        tooltipEl.classList.add(tooltipModel.yAlign)
                    } else {
                        tooltipEl.classList.add('no-transform')
                    }

                    function getBody (bodyItem) {
                        return bodyItem.lines
                    }
                    if (tooltipModel.body) {
                        const tooltipTitle = tooltipEl.querySelector('#chartjs-tooltip-title')
                        const bodyLines = tooltipModel.body.map(getBody)
                        let lines = ''
                        if (bodyLines.length && bodyLines[0].length) {
                            lines = bodyLines[0][0].split(': ')
                        }
                        const color = tooltipModel.labelColors[0].backgroundColor
                        tooltipTitle.innerHTML = `
                            <div style="display:flex; align-items:center; padding:${tooltipModel.yPadding}px ${tooltipModel.xPadding}px">
                                <div style="background-color:${color}; width: 10px; height: 10px; margin-right:5px; border-radius:50%;"></div>
                                ${lines[0]}
                            </div>`
                        productCode.value = lines[0]
                        const tooltipBody = tooltipEl.querySelector('#chartjs-tooltip-body')
                        tooltipBody.innerHTML = `<div style="padding:${tooltipModel.yPadding}px ${tooltipModel.xPadding}px">
                                                    ${__getDollar(lines[1])}
                                                </div>`
                    }

                    const position = this._chart.canvas.getBoundingClientRect()
                    tooltipEl.style.opacity = 1
                    tooltipEl.style.position = 'absolute'
                    tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px'
                    tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px'
                    tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily
                    tooltipEl.style.fontSize = tooltipModel.bodyFontSize + 'px'
                    tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle
                    tooltipEl.style.border = '1px solid #DBDBDB'
                    tooltipEl.style.borderRadius = '4px'
                    tooltipEl.style.pointerEvents = 'none'
                }

            },
            legend: {
                display: false
            },
            scales: {
                xAxes: [{
                    stacked: true,
                    gridLines: {
                        color: '#dbdbdb',
                        display: false,
                        drawTicks: false,
                        zeroLineColor: 'rgba(200, 200, 200, 0.65)',
                        zeroLineWidth: 0
                    },
                    ticks: {
                        padding: 8
                    }
                }],
                yAxes: [{
                    stacked: true,
                    gridLines: {
                        zeroLineColor: '#C8C8C8',
                        color: 'transparent',
                        drawTicks: false
                    },
                    ticks: {
                        callback: label => __toKFormat(label, 2),
                        beginAtZero: true,
                        autoSkip: false,
                        count: 6,
                        maxTicksLimit: 6,
                        padding: 10
                    }
                }]
            },
            plugins: {
                noDataBackground: {
                    display: true,
                    imageSrc: require('@/assets/images/empty.png'),
                    text: t('org.dashboard.No data')
                },
                datalabels: {
                    display: false
                }
            }
        }))

        return {
            chartData,
            chartOptions
        }
    }
}
</script>
